import { useMemo } from "react"
import { BrowserRouter, Routes, Route } from "react-router-dom"
import { Provider as UrqlProvider } from "urql"
import { TooltipProvider } from "@/components/ui/tooltip"
import { Toaster } from "@/components/ui/sonner"
import { ThemeProvider } from "@/components/theme-provider"
import { AuthContext, useAuthProvider } from "@/hooks/use-auth"
import { createClient, resetClient } from "@/lib/graphql/client"
import { AuthGate } from "@/components/auth-gate"
import { RootLayout } from "@/components/layout/root-layout"
import { DashboardPage } from "@/pages/dashboard/page"
import { SourcesPage } from "@/pages/sources/page"
import { SourceDetailPage } from "@/pages/sources/[id]/page"
import { DestinationsPage } from "@/pages/destinations/page"
import { DestinationDetailPage } from "@/pages/destinations/[id]/page"
import { SubscriptionsPage } from "@/pages/subscriptions/page"
import { SubscriptionDetailPage } from "@/pages/subscriptions/[id]/page"
import { EventsPage } from "@/pages/events/page"
import { EventDetailPage } from "@/pages/events/[id]/page"
import { DeliveriesPage } from "@/pages/deliveries/page"
import { DeliveryDetailPage } from "@/pages/deliveries/[id]/page"
import { DLQPage } from "@/pages/dlq/page"
import { ApplicationsPage } from "@/pages/applications/page"
import { ApplicationDetailPage } from "@/pages/applications/[id]/page"
import { EndpointsPage } from "@/pages/endpoints/page"
import { EndpointDetailPage } from "@/pages/endpoints/[id]/page"
import { MessagesPage } from "@/pages/messages/page"
import { MessageDetailPage } from "@/pages/messages/[id]/page"
import { OutboundDeliveriesPage } from "@/pages/outbound-deliveries/page"
import { OutboundDeliveryDetailPage } from "@/pages/outbound-deliveries/[id]/page"
import { OutboundDLQPage } from "@/pages/outbound-dlq/page"
import { OutboundDLQDetailPage } from "@/pages/outbound-dlq/[id]/page"
import { BookmarksPage } from "@/pages/bookmarks/page"
import { EventTypesPage } from "@/pages/event-types/page"
import { ApiKeysPage } from "@/pages/api-keys/page"
import { AlertRulesPage } from "@/pages/alert-rules/page"
import { MetaEventsPage } from "@/pages/meta-events/page"
import { TransformationsPage } from "@/pages/transformations/page"
import { StreamsPage } from "@/pages/streams/page"
import { StreamDetailPage } from "@/pages/streams/[id]/page"
import { AuditLogsPage } from "@/pages/audit-logs/page"
import { SettingsPage } from "@/pages/settings/page"
import { ShareLayout } from "@/pages/share/layout"
import { SharePage } from "@/pages/share/page"
import { PortalPage } from "@/pages/portal/page"
import { PortalLinksPage } from "@/pages/portal-links/page"
import { TunnelsPage } from "@/pages/tunnels/page"
import { VerificationTestPage } from "@/pages/verification-test/page"
import { ConfigPage } from "@/pages/config/page"
import { NotFoundPage } from "@/pages/not-found"
import { SignupPage } from "@/pages/auth/signup"
import { LoginPage } from "@/pages/auth/login"
import { OnboardingPage } from "@/pages/auth/onboarding"
import { CliAuthorizePage } from "@/pages/auth/cli-authorize"
import { VerifyPage } from "@/pages/auth/verify"
import { UpgradePage } from "@/pages/upgrade/page"
import { UsagePage } from "@/pages/usage/page"
import { IPAllowlistPage } from "@/pages/ip-allowlist/page"
import { FeatureGuard } from "@/components/feature-guard"
import { Code2, Bell, ExternalLink, Unplug, ShieldCheck } from "lucide-react"

function AuthedLayout() {
  return (
    <AuthGate>
      <RootLayout />
    </AuthGate>
  )
}

export default function App() {
  const auth = useAuthProvider()
  const client = useMemo(() => {
    return auth.isConfigured ? resetClient(auth.apiKey) : createClient()
  }, [auth.apiKey, auth.isConfigured])

  return (
    <UrqlProvider value={client}>
      <ThemeProvider>
        <AuthContext value={auth}>
          <TooltipProvider delayDuration={300}>
            <BrowserRouter>
              <Routes>
                {/* SaaS-only public auth routes; rendered for both binaries
                    but only meaningful in cmd/hivehook-saas. The
                    /api/v1/saas/auth/* endpoints are 404 on OSS so users
                    who navigate here on self-host get a clear error from
                    the form submit. */}
                <Route path="signup" element={<SignupPage />} />
                <Route path="login" element={<LoginPage />} />
                <Route path="onboarding" element={<OnboardingPage />} />
                <Route path="cli/authorize" element={<CliAuthorizePage />} />
                <Route path="auth/verify" element={<VerifyPage />} />

                <Route path="share" element={<ShareLayout />}>
                  <Route index element={<SharePage />} />
                </Route>
                <Route path="portal" element={<PortalPage />} />
                <Route element={<AuthedLayout />}>
                  <Route index element={<DashboardPage />} />
                  <Route path="upgrade" element={<UpgradePage />} />
                  <Route path="usage" element={<UsagePage />} />
                  <Route
                    path="ip-allowlist"
                    element={
                      <FeatureGuard
                        feature="ip_allowlist"
                        tier="torrent"
                        title="IP allowlist"
                        description="Restrict ingestion and API access to specific IPs or CIDR ranges."
                        icon={ShieldCheck}
                      >
                        <IPAllowlistPage />
                      </FeatureGuard>
                    }
                  />
                  <Route path="sources" element={<SourcesPage />} />
                  <Route path="sources/:id" element={<SourceDetailPage />} />
                  <Route path="destinations" element={<DestinationsPage />} />
                  <Route path="destinations/:id" element={<DestinationDetailPage />} />
                  <Route path="subscriptions" element={<SubscriptionsPage />} />
                  <Route path="subscriptions/:id" element={<SubscriptionDetailPage />} />
                  <Route path="events" element={<EventsPage />} />
                  <Route path="events/:id" element={<EventDetailPage />} />
                  <Route path="deliveries" element={<DeliveriesPage />} />
                  <Route path="deliveries/:id" element={<DeliveryDetailPage />} />
                  <Route path="bookmarks" element={<BookmarksPage />} />
                  <Route path="dlq" element={<DLQPage />} />
                  <Route path="applications" element={<ApplicationsPage />} />
                  <Route path="applications/:id" element={<ApplicationDetailPage />} />
                  <Route path="endpoints" element={<EndpointsPage />} />
                  <Route path="endpoints/:id" element={<EndpointDetailPage />} />
                  <Route path="messages" element={<MessagesPage />} />
                  <Route path="messages/:id" element={<MessageDetailPage />} />
                  <Route path="outbound-deliveries" element={<OutboundDeliveriesPage />} />
                  <Route path="outbound-deliveries/:id" element={<OutboundDeliveryDetailPage />} />
                  <Route path="streams" element={<StreamsPage />} />
                  <Route path="streams/:id" element={<StreamDetailPage />} />
                  <Route path="outbound-dlq" element={<OutboundDLQPage />} />
                  <Route path="outbound-dlq/:id" element={<OutboundDLQDetailPage />} />
                  <Route path="event-types" element={<EventTypesPage />} />
                  <Route path="api-keys" element={<ApiKeysPage />} />
                  <Route
                    path="transformations"
                    element={
                      <FeatureGuard
                        feature="transformations"
                        title="Transformations"
                        description="Reshape, filter, and enrich events with sandboxed JavaScript."
                        icon={Code2}
                      >
                        <TransformationsPage />
                      </FeatureGuard>
                    }
                  />
                  <Route
                    path="alert-rules"
                    element={
                      <FeatureGuard
                        feature="alerting"
                        title="Alert rules"
                        description="Get email and Slack alerts when deliveries fail or your dead-letter queue fills up."
                        icon={Bell}
                      >
                        <AlertRulesPage />
                      </FeatureGuard>
                    }
                  />
                  <Route path="meta-events" element={<MetaEventsPage />} />
                  <Route path="audit-logs" element={<AuditLogsPage />} />
                  <Route
                    path="portal-links"
                    element={
                      <FeatureGuard
                        feature="portal_links"
                        title="Portal links"
                        description="Give your customers a hosted page to manage their own webhook endpoints."
                        icon={ExternalLink}
                      >
                        <PortalLinksPage />
                      </FeatureGuard>
                    }
                  />
                  <Route
                    path="tunnels"
                    element={
                      <FeatureGuard
                        feature="tunnel"
                        title="Local-dev tunnel"
                        description="Relay live webhooks to your local machine while you build and debug."
                        icon={Unplug}
                      >
                        <TunnelsPage />
                      </FeatureGuard>
                    }
                  />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="verification-test" element={<VerificationTestPage />} />
                  <Route path="config" element={<ConfigPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </BrowserRouter>
            <Toaster />
          </TooltipProvider>
        </AuthContext>
      </ThemeProvider>
    </UrqlProvider>
  )
}
